import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Loader2, Users, ClipboardList, Stethoscope, CheckCircle2, Trash2, Download, ArrowLeft, RefreshCw, Mail, Link2, Copy, Megaphone, Send, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { METROS } from '../mock';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import AdminPromoTab from '../components/AdminPromoTab';
import AdminClinicTab from '../components/AdminClinicTab';
import api from '../api';

const STATUS = ['new', 'contacted', 'signed', 'declined'];
const CHANNELS = ['reddit', 'nextdoor', 'instagram', 'facebook', 'tiktok', 'flyer', 'influencer', 'email'];
const CITY_SLUG = { 'Portland, OR': 'portland', 'Eugene/Springfield, OR': 'eugene', 'San Francisco, CA': 'sanfrancisco', 'Other': 'other' };
const STATUS_STYLE = {
  new: 'bg-blue-50 text-blue-600 border-blue-200',
  contacted: 'bg-amber-50 text-amber-600 border-amber-200',
  signed: 'bg-green-50 text-green-600 border-green-200',
  declined: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};

function toCSV(rows, cols) {
  const head = cols.join(',');
  const body = rows.map((r) => cols.map((c) => `"${String(r[c] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  return `${head}\n${body}`;
}
function download(name, csv) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

const buildLink = (city, channel) => `${window.location.origin}/beta?city=${CITY_SLUG[city] || 'other'}&src=${channel}`;

const emailTemplate = (l) => ({
  subject: `Founding partner listing for ${l.clinic}`,
  body: `Hi ${l.contact_name || 'there'},\n\nThanks for your interest in Purrflect & Wooflect! We'd love to have ${l.clinic} as a founding partner in ${l.city}.\n\nFounding partners get 60 days free and a locked founder rate (Basic $79/mo or Premium $199/mo across both the cat and dog apps). Your clinic gets featured in our vet finder, in front of nearby pet parents actively looking for care.\n\nCan I set up your listing this week? Just reply to this email and we'll get you live.\n\nWarmly,\nThe Purrflect Team\nBigDog@happybigdogs.net`,
});

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-2xl bg-white p-5 card-soft" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4" /><span className="text-sm font-semibold">{label}</span></div>
    <p className="mt-2 text-3xl font-extrabold">{value ?? '—'}</p>
    {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

export default function Admin() {
  const nav = useNavigate();
  const { token, user, loading } = useApp();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [leads, setLeads] = useState([]);
  const [optins, setOptins] = useState([]);
  const [conv, setConv] = useState(null);
  const [busy, setBusy] = useState(true);
  const [campCity, setCampCity] = useState(METROS[0]);
  const [campChannel, setCampChannel] = useState(CHANNELS[0]);
  const [emailLead, setEmailLead] = useState(null);
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sending, setSending] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [s, w, l, c] = await Promise.all([
        api.get('/admin/stats'), api.get('/admin/waitlist'), api.get('/admin/vet-leads'), api.get('/admin/source-conversion'),
      ]);
      setStats(s.data); setWaitlist(w.data.items); setLeads(l.data.items); setConv(c.data);
      try { const o = await api.get('/admin/sms-optins'); setOptins(o.data.items); } catch {}
    } catch (e) {
      toast.error('Failed to load admin data');
    } finally { setBusy(false); }
  };

  useEffect(() => { if (token && user?.is_admin) load(); }, [token, user]);

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--coral))]" /></div>;
  if (!token) return <Navigate to="/auth" replace />;
  if (!user?.is_admin) return <Navigate to="/app" replace />;

  const setStatus = async (id, status) => {
    setLeads((p) => p.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await api.patch(`/admin/vet-leads/${id}`, { status }); toast.success('Lead updated'); }
    catch { toast.error('Update failed'); load(); }
  };
  const delLead = async (id) => { setLeads((p) => p.filter((l) => l.id !== id)); try { await api.delete(`/admin/vet-leads/${id}`); } catch {} };
  const delWait = async (id) => { setWaitlist((p) => p.filter((w) => w.id !== id)); try { await api.delete(`/admin/waitlist/${id}`); } catch {} };

  const copyLink = (text) => { navigator.clipboard.writeText(text); toast.success('Link copied'); };

  const downloadPack = async () => {
    try {
      const token = localStorage.getItem('pp_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/poster-pack`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'purrflect-poster-pack.zip'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Poster pack downloaded');
    } catch { toast.error('Download failed'); }
  };

  const openEmail = (l) => { const t = emailTemplate(l); setSubject(t.subject); setEmailBody(t.body); setEmailLead(l); };
  const sendEmail = async () => {
    setSending(true);
    try {
      const r = await api.post(`/admin/vet-leads/${emailLead.id}/email`, { subject, body: emailBody });
      setLeads((p) => p.map((l) => (l.id === emailLead.id ? { ...l, status: r.data.status || l.status } : l)));
      toast.success(`Email sent to ${emailLead.email}`);
      setEmailLead(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  const bySource = waitlist.reduce((a, w) => { const s = w.source || 'direct'; return { ...a, [s]: (a[s] || 0) + 1 }; }, {});

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <header className="sticky top-0 z-10 border-b border-border bg-background/95 px-5 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => nav('/app/profile')} className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-secondary"><ArrowLeft className="h-5 w-5" /></button>
            <h1 className="text-base font-extrabold">Leads Dashboard</h1>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-sm font-semibold hover:bg-accent" data-testid="admin-refresh"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-5">
        {/* Stat cards */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard icon={ClipboardList} label="Beta waitlist" value={stats?.waitlist} testid="stat-waitlist" />
          <StatCard icon={Stethoscope} label="Vet leads" value={stats?.vet_leads} sub={`${stats?.vet_signed ?? 0} signed`} testid="stat-leads" />
          <StatCard icon={Users} label="Users" value={stats?.users} testid="stat-users" />
          <StatCard icon={CheckCircle2} label="PRO users" value={stats?.pro_users} sub={`${stats?.paid_users ?? 0} paid · ${stats?.trial_users ?? 0} trial`} testid="stat-pro" />
        </div>

        {/* Tabs */}
        <div className="mt-6 flex gap-2 border-b border-border">
          {[['overview', 'Overview'], ['campaigns', 'Campaigns'], ['promos', 'Promo Codes'], ['clinics', 'Clinic Grants'], ['waitlist', `Waitlist (${waitlist.length})`], ['leads', `Vet Leads (${leads.length})`], ['optins', `SMS Opt-ins (${optins.length})`]].map(([k, lbl]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-bold transition ${tab === k ? 'border-[hsl(var(--coral))] text-[hsl(var(--coral))]' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>{lbl}</button>
          ))}
        </div>

        {busy ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--coral))]" /></div>
        ) : (
          <div className="mt-5">
            {tab === 'overview' && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-white p-5 card-soft">
                  <h3 className="font-extrabold">Waitlist by city</h3>
                  <ul className="mt-3 space-y-2">
                    {Object.entries(waitlist.reduce((a, w) => ({ ...a, [w.city]: (a[w.city] || 0) + 1 }), {})).map(([c, n]) => (
                      <li key={c} className="flex items-center justify-between text-sm"><span className="font-semibold">{c}</span><span className="rounded-full bg-accent px-2.5 py-0.5 font-bold text-[hsl(var(--coral))]">{n}</span></li>
                    ))}
                    {!waitlist.length && <li className="text-sm text-muted-foreground">No signups yet.</li>}
                  </ul>
                </div>
                <div className="rounded-2xl bg-white p-5 card-soft">
                  <h3 className="font-extrabold">Vet leads by status</h3>
                  <ul className="mt-3 space-y-2">
                    {STATUS.map((s) => (
                      <li key={s} className="flex items-center justify-between text-sm"><span className="font-semibold capitalize">{s}</span><span className="rounded-full bg-accent px-2.5 py-0.5 font-bold text-[hsl(var(--coral))]">{leads.filter((l) => (l.status || 'new') === s).length}</span></li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {tab === 'campaigns' && (
              <div className="space-y-4" data-testid="campaigns-tab">
                <div className="grid gap-4 lg:grid-cols-2">
                {/* Link builder */}
                <div className="rounded-2xl bg-white p-5 card-soft">
                  <h3 className="flex items-center gap-2 font-extrabold"><Link2 className="h-4 w-4 text-[hsl(var(--coral))]" />Campaign link builder</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Build a tracked link for each channel. Signups from it show up under that source.</p>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground">City</label>
                      <select value={campCity} onChange={(e) => setCampCity(e.target.value)} data-testid="camp-city" className="mt-1 h-10 w-full rounded-xl border border-input bg-white px-2 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
                        {METROS.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-muted-foreground">Channel</label>
                      <select value={campChannel} onChange={(e) => setCampChannel(e.target.value)} data-testid="camp-channel" className="mt-1 h-10 w-full rounded-xl border border-input bg-white px-2 text-sm capitalize outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
                        {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <div className="flex-1 truncate rounded-xl border border-border bg-secondary px-3 py-2.5 text-sm font-semibold" data-testid="camp-link">{buildLink(campCity, campChannel)}</div>
                    <button onClick={() => copyLink(buildLink(campCity, campChannel))} data-testid="camp-copy" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border bg-white hover:bg-accent"><Copy className="h-4 w-4" /></button>
                  </div>
                  <div className="mt-4">
                    <p className="text-xs font-semibold text-muted-foreground">Quick copy — all channels for {campCity}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {CHANNELS.map((c) => (
                        <button key={c} onClick={() => copyLink(buildLink(campCity, c))} className="rounded-full border border-border bg-white px-3 py-1.5 text-xs font-semibold capitalize hover:border-[hsl(var(--coral))] hover:text-[hsl(var(--coral))]">{c}</button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Source analytics + poster pack */}
                <div className="space-y-4">
                  <div className="rounded-2xl bg-white p-5 card-soft">
                    <h3 className="flex items-center gap-2 font-extrabold"><Megaphone className="h-4 w-4 text-[hsl(var(--coral))]" />Signups by source</h3>
                    <ul className="mt-3 space-y-2" data-testid="source-breakdown">
                      {Object.entries(bySource).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
                        <li key={s} className="flex items-center justify-between text-sm"><span className="font-semibold capitalize">{s}</span><span className="rounded-full bg-accent px-2.5 py-0.5 font-bold text-[hsl(var(--coral))]">{n}</span></li>
                      ))}
                      {!waitlist.length && <li className="text-sm text-muted-foreground">No signups yet.</li>}
                    </ul>
                  </div>
                  <div className="rounded-2xl bg-white p-5 card-soft">
                    <h3 className="font-extrabold">Poster pack</h3>
                    <p className="mt-1 text-sm text-muted-foreground">Download all city posters and QR codes as a zip, ready to print.</p>
                    <button onClick={downloadPack} data-testid="poster-pack-download" className="coral-btn mt-3 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white"><Download className="h-4 w-4" />Download poster pack (.zip)</button>
                  </div>
                </div>
                </div>

                <div className="rounded-2xl bg-white p-5 card-soft" data-testid="conversion-card">
                  <div className="flex items-center justify-between">
                    <h3 className="flex items-center gap-2 font-extrabold"><TrendingUp className="h-4 w-4 text-[hsl(var(--coral))]" />Source → PRO conversion</h3>
                    <span className="text-sm text-muted-foreground">{conv?.total_converted ?? 0}/{conv?.total_signups ?? 0} signups upgraded</span>
                  </div>
                  <div className="mt-3 overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary text-left text-xs uppercase text-muted-foreground">
                        <tr><th className="p-3">Source</th><th className="p-3">Signups</th><th className="p-3">Upgraded to PRO</th><th className="p-3">Conversion</th></tr>
                      </thead>
                      <tbody>
                        {(conv?.rows || []).map((r) => (
                          <tr key={r.source} className="border-t border-border">
                            <td className="p-3 font-semibold capitalize">{r.source}</td>
                            <td className="p-3">{r.signups}</td>
                            <td className="p-3">{r.converted}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <div className="h-2 w-20 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-[hsl(var(--coral))]" style={{ width: `${r.rate}%` }} /></div>
                                <span className="font-bold text-[hsl(var(--coral))]">{r.rate}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {!(conv?.rows || []).length && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No signups yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {tab === 'promos' && <AdminPromoTab />}

            {tab === 'clinics' && <AdminClinicTab />}

            {tab === 'waitlist' && (
              <div>
                <div className="mb-3 flex justify-end">
                  <button onClick={() => download('waitlist.csv', toCSV(waitlist, ['created_at', 'name', 'email', 'city', 'source', 'invite_code']))} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-sm font-semibold hover:bg-accent" data-testid="export-waitlist"><Download className="h-3.5 w-3.5" /> Export CSV</button>
                </div>
                <div className="overflow-x-auto rounded-2xl border border-border bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary text-left text-xs uppercase text-muted-foreground">
                      <tr><th className="p-3">Date</th><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">City</th><th className="p-3">Source</th><th className="p-3">Code</th><th className="p-3"></th></tr>
                    </thead>
                    <tbody data-testid="waitlist-table">
                      {waitlist.map((w) => (
                        <tr key={w.id} className="border-t border-border">
                          <td className="p-3 text-muted-foreground">{(w.created_at || '').slice(0, 10)}</td>
                          <td className="p-3 font-semibold">{w.name || '—'}</td>
                          <td className="p-3">{w.email}</td>
                          <td className="p-3">{w.city}</td>
                          <td className="p-3 text-muted-foreground">{w.source || '—'}</td>
                          <td className="p-3"><span className="rounded bg-accent px-1.5 py-0.5 text-xs font-bold text-[hsl(var(--coral))]">{w.invite_code}</span></td>
                          <td className="p-3"><button onClick={() => delWait(w.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button></td>
                        </tr>
                      ))}
                      {!waitlist.length && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No signups yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'optins' && (
              <div data-testid="optins-tab">
                <div className="mb-3 flex justify-end">
                  <button onClick={() => download('sms-optins.csv', toCSV(optins, ['created_at', 'name', 'phone', 'consent', 'consent_text', 'ip']))} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-sm font-semibold hover:bg-accent" data-testid="export-optins"><Download className="h-3.5 w-3.5" /> Export CSV</button>
                </div>
                <div className="overflow-x-auto rounded-2xl border border-border bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary text-left text-xs uppercase text-muted-foreground">
                      <tr><th className="p-3">Date &amp; time</th><th className="p-3">Name</th><th className="p-3">Phone</th><th className="p-3">Consent</th><th className="p-3">Consent text</th><th className="p-3">IP</th></tr>
                    </thead>
                    <tbody data-testid="optins-table">
                      {optins.map((o) => (
                        <tr key={o.id} className="border-t border-border align-top">
                          <td className="p-3 text-muted-foreground whitespace-nowrap">{(o.created_at || '').replace('T', ' ').slice(0, 16)}</td>
                          <td className="p-3 font-semibold">{o.name || '—'}</td>
                          <td className="p-3 font-mono">{o.phone}</td>
                          <td className="p-3">{o.consent ? <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-bold text-green-700">Yes</span> : '—'}</td>
                          <td className="p-3 max-w-md text-xs text-muted-foreground">{o.consent_text}</td>
                          <td className="p-3 text-xs text-muted-foreground">{o.ip || '—'}</td>
                        </tr>
                      ))}
                      {!optins.length && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No SMS opt-ins yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'leads' && (
              <div>
                <div className="mb-3 flex justify-end">
                  <button onClick={() => download('vet-leads.csv', toCSV(leads, ['created_at', 'clinic', 'contact_name', 'email', 'phone', 'city', 'plan_interest', 'status', 'message']))} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-sm font-semibold hover:bg-accent" data-testid="export-leads"><Download className="h-3.5 w-3.5" /> Export CSV</button>
                </div>
                <div className="overflow-x-auto rounded-2xl border border-border bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary text-left text-xs uppercase text-muted-foreground">
                      <tr><th className="p-3">Date</th><th className="p-3">Clinic</th><th className="p-3">Contact</th><th className="p-3">City</th><th className="p-3">Plan</th><th className="p-3">Status</th><th className="p-3"></th></tr>
                    </thead>
                    <tbody data-testid="leads-table">
                      {leads.map((l) => (
                        <tr key={l.id} className="border-t border-border align-top">
                          <td className="p-3 text-muted-foreground">{(l.created_at || '').slice(0, 10)}</td>
                          <td className="p-3"><div className="font-semibold">{l.clinic}</div>{l.message && <div className="mt-0.5 max-w-xs text-xs text-muted-foreground">{l.message}</div>}</td>
                          <td className="p-3">{l.contact_name || '—'}<div className="text-xs text-muted-foreground">{l.email}</div>{l.phone && <div className="text-xs text-muted-foreground">{l.phone}</div>}</td>
                          <td className="p-3">{l.city}</td>
                          <td className="p-3 capitalize">{l.plan_interest}</td>
                          <td className="p-3">
                            <select value={l.status || 'new'} onChange={(e) => setStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`}
                              className={`rounded-full border px-2.5 py-1 text-xs font-bold capitalize outline-none ${STATUS_STYLE[l.status || 'new']}`}>
                              {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                          <td className="p-3"><div className="flex items-center gap-2">
                            <button onClick={() => openEmail(l)} data-testid={`lead-email-${l.id}`} className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs font-semibold hover:bg-accent"><Mail className="h-3.5 w-3.5" />Email</button>
                            <button onClick={() => delLead(l.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                          </div></td>
                        </tr>
                      ))}
                      {!leads.length && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No leads yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={!!emailLead} onOpenChange={(v) => { if (!v) setEmailLead(null); }}>
        <DialogContent className="max-w-lg" data-testid="lead-email-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-[hsl(var(--coral))]" />Email {emailLead?.clinic}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">To: <span className="font-semibold text-foreground">{emailLead?.email}</span></p>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" className="mt-1 h-11 w-full rounded-xl border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Message</label>
              <textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={10} data-testid="email-body" className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setEmailLead(null)} className="rounded-xl border border-border bg-white px-4 py-2.5 text-sm font-bold">Cancel</button>
              <button onClick={sendEmail} disabled={sending} data-testid="email-send" className="coral-btn inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white disabled:opacity-70">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Send email
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
